import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import type {
  AccessGrantResponse,
  ChangeAccessModeRequest,
  CloseAccessResponse,
  OpenAccessRequest,
} from '@cattle/contracts'

import { post } from './apiClient'

/**
 * STANDING INSIDE SOMEONE ELSE'S BOOK (P5–P7).
 *
 * The most sensitive thing the platform can do, and the contract is shaped so
 * that it cannot be done quietly. Three properties are load-bearing and all
 * three are enforced here rather than by the screen that draws the modal:
 *
 * 1. **A reason is required.** `POST /platform/businesses/:id/access` writes an
 *    immutable `access_log` row FIRST and rejects an empty or three-character
 *    reason with a 400. There is no path through this module that opens a book
 *    without one.
 *
 * 2. **The grant is a separate credential.** The response carries a scoped
 *    Bearer token, and the console's own cookie session is deliberately NOT
 *    bound to it — so every in-book request must present `grant.token`, and a
 *    console request that forgets it simply is not inside the book. That is why
 *    the token lives in memory here and is threaded into `apiClient` explicitly
 *    rather than being installed as a default header.
 *
 * 3. **Read-only is the default and edit is a second act.** `mode` starts
 *    `READ_ONLY`; escalating calls P6, which is PLATFORM_OWNER-only server-side
 *    and is itself written to the trader's own audit log.
 *
 * A grant lasts one hour. Nothing is persisted: reloading the page ends the
 * visit, which is the correct behaviour for a credential that must be asked for
 * out loud each time.
 */

export interface AccessGrant extends AccessGrantResponse {
  /** Client-side clock for the banner. The server owns `expiresAt`. */
  openedAtMs: number
}

interface PlatformAccessState {
  grant: AccessGrant | null
  /** Opens a book. `businessId` is the tenant ULID, not the slug. */
  open: (businessId: string, request: OpenAccessRequest) => Promise<AccessGrant>
  /** P6 · "Switch to edit". Refused server-side for anyone but the owner. */
  setMode: (request: ChangeAccessModeRequest) => Promise<void>
  /** P7 · Ends the visit and stamps the log. */
  close: () => Promise<CloseAccessResponse | null>
}

const PlatformAccessContext = createContext<PlatformAccessState | null>(null)

function isLive(grant: AccessGrant | null): grant is AccessGrant {
  return grant !== null && Date.parse(grant.expiresAt) > Date.now()
}

export function PlatformAccessProvider({ children }: { children: ReactNode }) {
  const [grant, setGrant] = useState<AccessGrant | null>(null)
  const queryClient = useQueryClient()

  const open = useCallback(
    async (businessId: string, request: OpenAccessRequest) => {
      const response = await post<AccessGrantResponse>(
        `/platform/businesses/${businessId}/access`,
        request,
      )
      const next: AccessGrant = { ...response, openedAtMs: Date.parse(response.openedAt) }
      setGrant(next)
      // Anything cached for this book was cached without a grant, i.e. without
      // the money the grant unlocks. Refetch rather than show the stripped copy.
      await queryClient.invalidateQueries({ queryKey: ['book'] })
      return next
    },
    [queryClient],
  )

  const setMode = useCallback(
    async (request: ChangeAccessModeRequest) => {
      if (!grant) return
      const response = await post<AccessGrantResponse>(
        `/platform/access/${grant.accessId}/mode`,
        request,
      )
      setGrant((current) =>
        current ? { ...current, mode: response.mode, token: response.token ?? current.token } : null,
      )
    },
    [grant],
  )

  const close = useCallback(async () => {
    if (!grant) return null
    try {
      return await post<CloseAccessResponse>(`/platform/access/${grant.accessId}/close`)
    } finally {
      setGrant(null)
      await queryClient.invalidateQueries({ queryKey: ['book'] })
    }
  }, [grant, queryClient])

  const value = useMemo<PlatformAccessState>(
    () => ({ grant: isLive(grant) ? grant : null, open, setMode, close }),
    [grant, open, setMode, close],
  )

  return (
    <PlatformAccessContext.Provider value={value}>{children}</PlatformAccessContext.Provider>
  )
}

export function usePlatformAccess(): PlatformAccessState {
  const value = useContext(PlatformAccessContext)
  if (!value) throw new Error('usePlatformAccess outside PlatformAccessProvider')
  return value
}

/**
 * The live grant for one business, or `null`.
 *
 * `null` is not an error state — it is the normal answer everywhere except
 * inside P-04. A book with no grant is a book this console may not read, and
 * the screen that asks renders its empty state rather than inventing figures.
 */
export function useAccessGrantFor(slug: string): AccessGrant | null {
  const { grant } = usePlatformAccess()
  return grant && grant.tenantSlug === slug ? grant : null
}
